import { Colors, MultiSelectOutlined, RadioGroup, Text, TextInput } from '@amelio/ui-library';

import { BULK_PERCENTAGE_MAX, BULK_PERCENTAGE_MIN } from '../calibrationConstants';
import { MODAL_FIELD_STACK } from '../calibrationStyles';

import type { CalibrationAdjustmentDirection, CalibrationManager } from '@/types/calibration';
import type { OptionProps, RadioGroupOption } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { toReaderLocale } from '@/utils/numberFormat';
import { getFullName } from '@/utils/user';

export interface BulkAdjustmentFieldsProps {
    managers: CalibrationManager[];
    selectedManagerIds: string[];
    onToggleManager: (managerId: string) => void;
    direction: CalibrationAdjustmentDirection;
    onDirectionChange: (direction: CalibrationAdjustmentDirection) => void;
    percentageInput: string;
    onPercentageChange: (value: string) => void;
    language: string;
}

/** The three inputs the bulk sweep is built from: whom it targets, which way, and by how much. */
export const BulkAdjustmentFields = ({
    managers,
    selectedManagerIds,
    onToggleManager,
    direction,
    onDirectionChange,
    percentageInput,
    onPercentageChange,
    language,
}: BulkAdjustmentFieldsProps) => {
    const { t } = useAmelioTranslations();

    const percentageFormatter = new Intl.NumberFormat(toReaderLocale(language), {
        maximumFractionDigits: 1,
    });

    const managerOptions: OptionProps[] = managers.map((manager) => ({
        value: manager.managerId,
        label: getFullName(manager),
    }));

    const directionOptions: RadioGroupOption[] = [
        { value: 'Increase', label: t('calibration.bulk.directionIncrease') },
        { value: 'Decrease', label: t('calibration.bulk.directionDecrease') },
    ];

    return (
        <>
            <div className={MODAL_FIELD_STACK}>
                <Text weight={'medium'}>{t('calibration.bulk.managersLabel')}</Text>

                <MultiSelectOutlined
                    options={managerOptions}
                    values={selectedManagerIds}
                    onChange={onToggleManager}
                    placeholder={t('calibration.bulk.managersAllSelected')}
                    showSelection
                    dataTestId={'calibration-bulk-managers'}
                />
            </div>

            <div className={MODAL_FIELD_STACK}>
                <Text weight={'medium'}>{t('calibration.bulk.directionLabel')}</Text>

                <RadioGroup
                    name={'calibration-bulk-direction'}
                    options={directionOptions}
                    value={direction}
                    onChange={(value) => onDirectionChange(value as CalibrationAdjustmentDirection)}
                    orientation={'horizontal'}
                    dataTestId={'calibration-bulk-direction'}
                />
            </div>

            <div className={MODAL_FIELD_STACK}>
                <Text weight={'medium'}>{t('calibration.bulk.percentageLabel')}</Text>

                <div className={'w-32'}>
                    <TextInput
                        type={'number'}
                        value={percentageInput}
                        onChange={onPercentageChange}
                        ariaLabel={t('calibration.bulk.percentageLabel')}
                        dataTestId={'calibration-bulk-percentage'}
                    />
                </div>

                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                >
                    {t('calibration.bulk.percentageHint', {
                        min: percentageFormatter.format(BULK_PERCENTAGE_MIN),
                        max: percentageFormatter.format(BULK_PERCENTAGE_MAX),
                    })}
                </Text>
            </div>
        </>
    );
};
